/**
 * menahub-attribution.js — first-touch attribution for custom checkout
 * v1.0.0 — 2026-05-07
 *
 * Why this exists:
 *   Our Tap-payments checkout (page.custom-checkout) bypasses Shopify's
 *   hosted /checkout, which means Shopify never sees the buyer's
 *   landing URL, referrer, or UTM tags. As a result the Order →
 *   Conversion Summary panel in Shopify Admin stays blank.
 *
 *   We solve that by capturing attribution data on the FIRST page the
 *   visitor lands on, persisting it in localStorage, and replaying it
 *   to the backend when they submit the charge. The backend then
 *   forwards it to the Shopify Admin API as `landing_site`,
 *   `referring_site`, `client_details` and UTM `note_attributes`.
 *
 * Attribution model: first-touch.
 *   - On the first visit we store landing URL + referrer + UTM tags.
 *   - On later pages we ONLY refresh UTM tags if the new URL has them
 *     (last-non-empty-UTM wins — prevents overwriting paid-ad UTMs
 *     with a later organic page view).
 *   - Cleared once an order successfully completes (the success page
 *     calls window.MenahubAttribution.clear()).
 *
 * Storage key: localStorage["menahub_attr"] — JSON blob, max ~3KB.
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'menahub_attr';
  var MAX_AGE_MS  = 30 * 24 * 60 * 60 * 1000; // 30 days

  function readStore() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      // Expire stale data
      if (parsed.captured_at && (Date.now() - parsed.captured_at) > MAX_AGE_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch (e) { return null; }
  }

  function writeStore(obj) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (e) { /* quota / private mode — ignore */ }
  }

  function parseUtm(href) {
    var out = {};
    try {
      var u = new URL(href);
      // UTM tags + ad click ids (gclid = Google Ads, fbclid = Meta/Instagram).
      // Click ids identify the paid channel + keyword even without UTM tags.
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
       'gclid', 'gbraid', 'wbraid', 'fbclid'].forEach(function (k) {
        var v = u.searchParams.get(k);
        if (v) out[k] = v.slice(0, 256);
      });
    } catch (e) { /* bad URL */ }
    return out;
  }

  function capture() {
    var existing = readStore();
    var url      = window.location.href;
    var referrer = document.referrer || '';
    var newUtm   = parseUtm(url);
    var hasNewUtm = Object.keys(newUtm).length > 0;

    if (existing) {
      // Refresh ONLY UTM if new URL has them (last-non-empty-UTM wins)
      if (hasNewUtm) {
        Object.assign(existing, newUtm);
        existing.utm_refreshed_at = Date.now();
        writeStore(existing);
      }
      return existing;
    }

    // First visit: snapshot everything
    var snapshot = {
      landing_site:    url.slice(0, 2048),
      referring_site:  referrer.slice(0, 2048),
      user_agent:      (navigator.userAgent || '').slice(0, 512),
      accept_language: (navigator.language || '').slice(0, 64),
      captured_at:     Date.now()
    };
    Object.assign(snapshot, newUtm);
    writeStore(snapshot);
    return snapshot;
  }

  function get() {
    return readStore() || capture();
  }

  function clear() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  // Public API
  window.MenahubAttribution = { capture: capture, get: get, clear: clear };

  // Run immediately (we want the snapshot taken before any link is clicked)
  capture();
})();
